import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./transactionStyles.module.css";
import Spinner from "../common/Spinner";

const hasDisplayValue = (value) => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const getMerchantName = (transaction) => {
  const merchant =
    transaction?.Merchant ||
    transaction?.creditorName ||
    transaction?.debtorName ||
    transaction?.proprietaryBankTransactionCode?.replace(/_/g, " ") ||
    transaction?.remittanceInformationUnstructuredArray;

  return typeof merchant === "string"
    ? merchant.replace(/'/g, "")
    : "Unknown Merchant";
};

const toDisplayValue = (value) => {
  if (!hasDisplayValue(value)) {
    return "";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => toDisplayValue(item))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const displayValue = toDisplayValue(nestedValue);

        return hasDisplayValue(displayValue) ? `${key}: ${displayValue}` : null;
      })
      .filter(Boolean)
      .join(", ");
  }

  return String(value);
};

const parseAdditionalInformation = (additionalInformation) => {
  try {
    if (!hasDisplayValue(additionalInformation)) {
      return [];
    }

    if (
      typeof additionalInformation === "object" &&
      !Array.isArray(additionalInformation)
    ) {
      return Object.entries(additionalInformation)
        .filter(([, value]) => hasDisplayValue(value))
        .map(([key, value]) => ({
          key,
          value: toDisplayValue(value),
        }));
    }

    const normalizedInformation = Array.isArray(additionalInformation)
      ? additionalInformation.map((info) => toDisplayValue(info)).join(", ")
      : String(additionalInformation).trim();

    if (!normalizedInformation) {
      return [];
    }

    if (
      normalizedInformation.startsWith("{") ||
      normalizedInformation.startsWith("[")
    ) {
      try {
        return parseAdditionalInformation(JSON.parse(normalizedInformation));
      } catch {
        return [
          {
            key: "Additional information",
            value: normalizedInformation,
          },
        ];
      }
    }

    if (!normalizedInformation.includes(": ")) {
      return [
        {
          key: "Additional information",
          value: normalizedInformation,
        },
      ];
    }

    return normalizedInformation
      .split(", ")
      .map((info) => info.replace(/[\[\]']+/g, ""))
      .map((info) => {
        const [key, ...valueParts] = info.split(": ");
        const value = valueParts.join(": ");

        if (!hasDisplayValue(key) || !hasDisplayValue(value)) {
          return {
            key: "Additional information",
            value: info,
          };
        }

        return { key, value };
      });
  } catch (error) {
    console.error("Failed to parse additional information as JSON:", error);
    return [
      {
        key: "Additional information",
        value: toDisplayValue(additionalInformation),
      },
    ].filter(({ value }) => hasDisplayValue(value));
  }
};

const getTransactionDetailRows = (transaction) => {
  if (!transaction) {
    return [];
  }

  const amount = [
    transaction?.transactionAmount?.amount,
    transaction?.transactionAmount?.currency,
  ]
    .filter(hasDisplayValue)
    .join(" ");

  const baseRows = [
    { key: "Merchant", value: getMerchantName(transaction) },
    { key: "Booking date", value: transaction?.bookingDate },
    { key: "Value date", value: transaction?.valueDate },
    { key: "Amount", value: amount },
    { key: "Account IBAN", value: transaction?.iban },
    { key: "Debtor name", value: transaction?.debtorName },
    { key: "Creditor name", value: transaction?.creditorName },
    { key: "Debtor IBAN", value: transaction?.debtorAccount?.iban },
    { key: "Creditor IBAN", value: transaction?.creditorAccount?.iban },
    {
      key: "Reference / Mededeling",
      value:
        transaction?.Mededeling ||
        transaction?.remittanceInformationUnstructured,
    },
    { key: "Reference", value: transaction?.transactionId },
    { key: "Institution", value: transaction?.institution_id },
  ].filter(({ value }) => hasDisplayValue(value));

  return [
    ...baseRows,
    ...parseAdditionalInformation(transaction.additionalInformation),
  ];
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 950);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  return isMobile;
};

const TransactionTable = ({
  transactions = [],
  account,
  handleSearchTransaction,
  setSearchTransaction,
  sortOrder,
  setSortOrder,
  loading,
  onScrollToBottom,
}) => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const transactionCardsRef = useRef(null);
  const scrollPositionRef = useRef(0); // Ref to store the scroll position
  const selectedTransactionDetails = useMemo(
    () => getTransactionDetailRows(selectedTransaction),
    [selectedTransaction],
  );

  const toggleAdditionalInformation = (e, transaction) => {
    e.stopPropagation();
    setSelectedTransaction(transaction);
  };

  const closeModal = () => {
    setSelectedTransaction(null);
  };

  // Memoize filtered transactions to prevent unnecessary re-renders
  const filteredTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];

    let sortedTransactions = [...transactions];
    if (sortOrder === "asc") {
      sortedTransactions.sort(
        (a, b) => new Date(a.bookingDate) - new Date(b.bookingDate),
      );
    } else {
      sortedTransactions.sort(
        (a, b) => new Date(b.bookingDate) - new Date(a.bookingDate),
      );
    }

    const trimmedSearchTerm = handleSearchTransaction.trim();
    if (!trimmedSearchTerm) return sortedTransactions; // If no search term, return all sorted transactions

    return sortedTransactions.filter((transaction) => {
      const merchantName = getMerchantName(transaction);

      const transactionAmount =
        transaction?.transactionAmount?.amount?.toString() || "";

      return (
        merchantName.toLowerCase().includes(trimmedSearchTerm.toLowerCase()) ||
        transactionAmount.includes(trimmedSearchTerm)
      );
    });
  }, [transactions, sortOrder, handleSearchTransaction]);

  const handleScroll = () => {
    if (transactionCardsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        transactionCardsRef.current;
      /*     console.log(
        "scrollTop:",
        scrollTop,
        "scrollHeight:",
        scrollHeight,
        "clientHeight:",
        clientHeight
      ); */
      // Save the current scroll position
      scrollPositionRef.current = scrollTop;

      // Check if the user has scrolled near the bottom
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        if (onScrollToBottom) {
          onScrollToBottom(scrollTop, scrollHeight, clientHeight); // Pass scroll details to parent
        }
      }
    }
  };

  useEffect(() => {
    // Restore the scroll position after new transactions are added
    if (transactionCardsRef.current) {
      transactionCardsRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [transactions]);

  return (
    <div className={styles.transactionContainer}>
      <div className={styles.searchAndSortContainer}>
        <input
          type="text"
          placeholder="Search Transactions"
          className={styles.searchInput}
          value={handleSearchTransaction}
          onChange={(e) => {
            e.stopPropagation();
            setSearchTransaction(e.target.value);
          }}
        />
        <div className={styles.sortingContainer}>
          <label htmlFor="sortOrder" className={styles.sortLabel}>
            Sort by Date:
          </label>
          <select
            id="sortOrder"
            className={styles.sortDropdown}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      <div
        className={styles.transactionCards}
        ref={transactionCardsRef}
        onScroll={handleScroll} // Attach scroll handler
      >
        {filteredTransactions?.map((transaction, transactionIndex) => {
          const merchantName = getMerchantName(transaction);

          const formattedMerchantName =
            merchantName.charAt(0).toUpperCase() +
            merchantName.slice(1).toLowerCase();

          return (
            <div
              key={transactionIndex}
              className={`${styles.transactionCard} ${styles.fadeIn}`}
              onClick={(e) => toggleAdditionalInformation(e, transaction)}
            >
              <div className={styles.transactionDetails}>
                <h4 className={styles.merchantName}>{formattedMerchantName}</h4>
                <p className={styles.transactionDate}>
                  <strong>Date:</strong> {transaction.bookingDate}
                </p>
                <p className={styles.transactionAmount}>
                  <strong>Amount:</strong>{" "}
                  {transaction.transactionAmount?.amount}{" "}
                  {transaction.transactionAmount?.currency}
                </p>
              </div>
            </div>
          );
        })}
        {loading && <Spinner />}
        {!loading && filteredTransactions?.length < 1 && (
          <h2 className={styles.noTransactionsFound}>No transactions found</h2>
        )}
      </div>

      {selectedTransaction && (
        <div
          className={styles.modal}
          onClick={(e) => {
            e.stopPropagation();
            closeModal();
          }}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className={styles.close}
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
              }}
            >
              &times;
            </span>
            <h2>Transaction details</h2>
            {selectedTransactionDetails.length > 0 ? (
              <div className={styles.additionalInfoContainer}>
                {selectedTransactionDetails.map(({ key, value }, index) => (
                  <div key={index} className={styles.additionalInfoRow}>
                    <span className={styles.additionalInfoKey}>{key}</span>
                    <span className={styles.additionalInfoValue}>{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p>No transaction details available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
